import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import appointmentService from '../services/appointmentService';

const AppointmentScheduler = ({ vehicleInfo = null }) => {
  const [selectedDate, setSelectedDate] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedTime, setSelectedTime] = useState('');
  const [serviceType, setServiceType] = useState('service');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    vehicleInfo: vehicleInfo || '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [blockedReason, setBlockedReason] = useState('');
  const [confirmedAppointment, setConfirmedAppointment] = useState(null);

  // Get minimum date (tomorrow)
  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  // Get maximum date (60 days from now)
  const getMaxDate = () => {
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 60);
    return maxDate.toISOString().split('T')[0];
  };

  // Load available time slots when date changes
  useEffect(() => {
    if (selectedDate) {
      loadAvailableSlots();
    }
  }, [selectedDate, serviceType]);

  const loadAvailableSlots = async () => {
    setLoading(true);
    setSelectedTime('');
    setBlockedReason('');
    
    const result = await appointmentService.getAvailableSlots(selectedDate, serviceType);
    
    if (result.success) {
      if (result.blocked) {
        setBlockedReason(result.reason || 'This date is not available');
        setAvailableSlots([]);
      } else {
        setAvailableSlots(result.data);
      }
    } else {
      setError('Failed to load available times');
    }
    
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const appointmentData = {
      ...formData,
      date: selectedDate,
      time: selectedTime,
      serviceType
    };

    const result = await appointmentService.bookAppointment(appointmentData);

    if (result.success) {
      // Fire Google Ads conversion event
      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('event', 'conversion', {
          'send_to': 'AW-17797421129/LrUYCIi_tdMbEMmwvKZC'
        });
        console.log('✅ Google Ads conversion event fired');
      }
      
      // Store appointment details before resetting
      setConfirmedAppointment({
        date: selectedDate,
        time: selectedTime
      });
      setSuccess(true);
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        vehicleInfo: vehicleInfo || '',
        notes: ''
      });
      setSelectedDate('');
      setSelectedTime('');
      setAvailableSlots([]);
    } else {
      setError(result.error || 'Failed to book appointment');
    }

    setLoading(false);
  };

  const serviceTypes = appointmentService.getServiceTypes();

  if (success && confirmedAppointment) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center">
        <div className="text-green-600 text-5xl mb-4">✅</div>
        <h3 className="text-2xl font-bold text-green-900 mb-2">Appointment Confirmed!</h3>
        <p className="text-green-700 mb-4">
          We've scheduled your appointment for {new Date(confirmedAppointment.date).toLocaleDateString()} 
          {' '}at {appointmentService.formatTime(confirmedAppointment.time)}.
        </p>
        <p className="text-green-600">
          You'll receive a confirmation email shortly with all the details.
        </p>
        <button
          onClick={() => {
            setSuccess(false);
            setConfirmedAppointment(null);
          }}
          className="mt-6 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
        >
          Book Another Appointment
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Service Type Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Select Service Type
        </label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {serviceTypes.map((type) => (
            <button
              key={type.value}
              type="button"
              onClick={() => setServiceType(type.value)}
              className={`p-3 rounded-lg border-2 transition-all ${
                serviceType === type.value
                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="text-2xl mb-1">{type.icon}</div>
              <div className="text-sm font-medium">{type.label}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Date Selection */}
      <div>
        <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-2">
          Select Date
        </label>
        <input
          type="date"
          id="date"
          min={getMinDate()}
          max={getMaxDate()}
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          required
        />
      </div>

      {/* Time Slot Selection */}
      {selectedDate && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Time
          </label>
          
          {loading ? (
            <div className="text-center py-4 text-gray-500">Loading available times...</div>
          ) : blockedReason ? (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-yellow-800">
              {blockedReason}
            </div>
          ) : availableSlots.length > 0 ? (
            <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
              {availableSlots.map((slot) => (
                <button
                  key={slot.time}
                  type="button"
                  onClick={() => setSelectedTime(slot.time)}
                  className={`p-2 rounded-lg border transition-all ${
                    selectedTime === slot.time
                      ? 'border-primary-500 bg-primary-500 text-white'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {slot.display}
                </button>
              ))}
            </div>
          ) : (
            <div className="bg-gray-50 rounded-lg p-4 text-gray-500 text-center">
              No available times for this date. Please select another date.
            </div>
          )}
        </div>
      )}

      {/* Contact Information */}
      {selectedTime && (
        <>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                Full Name *
              </label>
              <input
                type="text"
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                required
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number *
              </label>
              <input
                type="tel"
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                required
              />
            </div>
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              Email Address *
            </label>
            <input
              type="email"
              id="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              required
            />
          </div>

          <div>
            <label htmlFor="vehicleInfo" className="block text-sm font-medium text-gray-700 mb-2">
              Vehicle Information
            </label>
            <input
              type="text"
              id="vehicleInfo"
              value={formData.vehicleInfo}
              onChange={(e) => setFormData({ ...formData, vehicleInfo: e.target.value })}
              placeholder="Year, Make, Model, or Stock Number"
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
              Additional Notes
            </label>
            <textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={4}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              placeholder="Any specific concerns or requests..."
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !selectedTime || !formData.name || !formData.email || !formData.phone}
            className={`w-full py-3 px-6 rounded-lg font-semibold transition-all ${
              loading || !selectedTime || !formData.name || !formData.email || !formData.phone
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-primary-600 hover:bg-primary-700 text-white'
            }`}
          >
            {loading ? 'Booking...' : 'Confirm Appointment'}
          </button>
        </>
      )}
    </form>
  );
};

export default AppointmentScheduler;